import Swiper from 'swiper';
import {
	Navigation,
	Pagination,
	Autoplay,
	EffectFade,
	EffectCoverflow,
} from 'swiper/modules';

/**
 * Initialize Swiper on a specific element.
 *
 * @param {HTMLElement} element The element to initialize Swiper on.
 * @param {Object}      options Optional Swiper settings to override defaults.
 */
export function initSlider(element, options = {}) {
	if (!element) return null;

	// Default internal configuration
	const defaults = {
		modules: [
			Navigation,
			Pagination,
			Autoplay,
			EffectFade,
			EffectCoverflow,
		],
		loop: false,
		effect: 'slide',
		slidesPerView: 1,
		spaceBetween: 0,
		autoplay: {
			delay: 3000,
			disableOnInteraction: false,
			pauseOnMouseEnter: true,
		},
		pagination: {
			el: element.querySelector('.swiper-pagination'),
			clickable: true,
		},
		navigation: {
			nextEl: element.querySelector('.swiper-button-next'),
			prevEl: element.querySelector('.swiper-button-prev'),
		},
	};

	// Merge defaults with provided options
	// Deep merge is better for objects like navigation/pagination
	const config = {
		...defaults,
		...options,
		autoplay:
			options.autoplay === false
				? false
				: { ...defaults.autoplay, ...options.autoplay },
		pagination:
			options.pagination === false
				? false
				: { ...defaults.pagination, ...options.pagination },
		navigation:
			options.navigation === false
				? false
				: { ...defaults.navigation, ...options.navigation },
	};

	if (config.effect === 'fade') {
		config.slidesPerView = 1;
		config.speed = 800;
		config.fadeEffect = {
			crossFade: true,
		};
	}

	if (config.effect === 'coverflow') {
		config.centeredSlides = true;
		if (!options.slidesPerView) {
			config.slidesPerView = 'auto';
		}
		config.coverflowEffect = {
			rotate: 30,
			slideShadows: true,
		};
	}

	const swiper = new Swiper(element, config);

	// Play/Pause button logic
	const playBtn = element.querySelector('.swiper-button-play');
	const pauseBtn = element.querySelector('.swiper-button-pause');

	const updateButtons = (playing) => {
		if (playBtn && pauseBtn) {
			if (playing) {
				pauseBtn.style.display = 'flex';
				playBtn.style.display = 'none';
			} else {
				pauseBtn.style.display = 'none';
				playBtn.style.display = 'flex';
			}
		}
	};

	// Initial state
	const isAutoplayRunning = swiper.autoplay && swiper.autoplay.running;
	updateButtons(isAutoplayRunning);

	if (pauseBtn) {
		pauseBtn.addEventListener('click', (e) => {
			e.preventDefault();
			if (swiper.autoplay) {
				swiper.autoplay.stop();
				updateButtons(false);
			}
		});
	}

	if (playBtn) {
		playBtn.addEventListener('click', (e) => {
			e.preventDefault();
			if (swiper.autoplay) {
				swiper.autoplay.start();
				updateButtons(true);
			}
		});
	}

	// Listen for swatch state changes
	swiper.on('autoplayStart', () => {
		updateButtons(true);
	});
	swiper.on('autoplayStop', () => {
		updateButtons(false);
	});

	return swiper;
}

/**
 * Initialize all sliders on a page (standard for view.js).
 */
export function initAllSliders(selector = '.mnorwise-slider') {
	const sliders = document.querySelectorAll(selector);
	sliders.forEach((slider) => {
		if (slider.classList.contains('swiper-initialized')) return;

		// Extract data from attributes (for view.js)
		const {
			autoplay,
			delay,
			loop,
			effect,
			arrows,
			pagination,
			slidesPerView,
			slidesPerViewTablet,
			slidesPerViewMobile,
			spaceBetween,
			autoplayProgress,
		} = slider.dataset;

		initSlider(slider, {
			loop:
				loop === 'true' &&
				slider.querySelectorAll('.swiper-slide').length > 1,
			effect: effect || 'slide',
			autoplay:
				autoplay === 'true'
					? {
							delay: parseInt(delay, 10) || 3000,
							disableOnInteraction: false,
							pauseOnMouseEnter: true,
						}
					: false,
			pagination: pagination === 'true' ? {} : false,
			navigation: arrows === 'true' ? {} : false,
			slidesPerView: slidesPerView
				? slidesPerView === 'auto'
					? 'auto'
					: parseFloat(slidesPerView)
				: 1,
			breakpoints: {
				// when window width is >= 0px (mobile)
				0: {
					slidesPerView: slidesPerViewMobile
						? parseFloat(slidesPerViewMobile)
						: 1,
				},
				// when window width is >= 782px (tablet)
				782: {
					slidesPerView: slidesPerViewTablet
						? parseFloat(slidesPerViewTablet)
						: 1,
				},
				// when window width is >= 1080px (desktop)
				1080: {
					slidesPerView: slidesPerView
						? slidesPerView === 'auto'
							? 'auto'
							: parseFloat(slidesPerView)
						: 1,
				},
			},
			spaceBetween: spaceBetween ? parseInt(spaceBetween, 10) : 0,
			on:
				autoplayProgress === 'true'
					? {
							autoplayTimeLeft(s, time, progress) {
								const progressEl = slider.querySelector(
									'.autoplay-progress span'
								);
								if (progressEl) {
									progressEl.style.setProperty(
										'--progress',
										1 - progress
									);
								}
							},
						}
					: {},
		});
	});
}
